import type { ComponentType } from 'react';
import { Info } from 'lucide-react';
import ArrowRightPrestartIcon from '@/assets/icons/pre-start/arrow-right.svg?react';
import TennisPrestartIcon from '@/assets/icons/pre-start/tennis.svg?react';
import TrophyPrestartIcon from '@/assets/icons/pre-start/trophy.svg?react';
import ArrowRightGuideIcon from '../assets/icons/resources-guide-arrow-right.svg?react';
import { cn, openUrlInDefaultBrowser } from '@/utils';
import type { GrandSlamResource, GrandSlamResourceIcon } from '../constants/prestart.constants';
import { GRAND_SLAM_RESOURCE_LUCIDE_CLASS } from '../constants/prestart.constants';
import { ResourceGuideIconBadge } from './resources';

const PRESTART_RESOURCE_ICONS = {
	info: Info,
	scoring: TennisPrestartIcon,
	prize: TrophyPrestartIcon,
} as const satisfies Partial<
	Record<GrandSlamResourceIcon, ComponentType<{ className?: string; strokeWidth?: number }>>
>;

type GrandSlamResourceLinkCardVariant = 'prestart-mobile' | 'prestart-desktop' | 'guide';

interface GrandSlamResourceLinkCardProps extends GrandSlamResource {
	variant: GrandSlamResourceLinkCardVariant;
}

function PrestartResourceIconBadge({
	icon,
	iconClassName,
}: {
	icon: keyof typeof PRESTART_RESOURCE_ICONS;
	iconClassName: string;
}) {
	const Icon = PRESTART_RESOURCE_ICONS[icon];
	const lucideClassName = GRAND_SLAM_RESOURCE_LUCIDE_CLASS[icon];

	return (
		<div
			className={cn(
				'flex size-10 shrink-0 items-center justify-center rounded-full border-[0.5px]',
				iconClassName,
				lucideClassName
			)}
		>
			<Icon className="size-6" strokeWidth={1.5} />
		</div>
	);
}

export function GrandSlamResourceLinkCard({
	title,
	description,
	href,
	icon,
	iconClassName,
	iconUrl,
	variant,
}: GrandSlamResourceLinkCardProps) {
	const isDesktopPrestart = variant === 'prestart-desktop';
	const useGuideIcons = variant === 'guide';

	return (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			onClick={(event) => {
				event.preventDefault();
				openUrlInDefaultBrowser(href);
			}}
			className={cn(
				'rounded-[12px] border-[0.5px] border-[#DCE3F9] bg-white px-4 shadow-[0_1px_2px_rgba(12,12,13,0.05)] transition-colors hover:bg-[#F7F8FC] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#2667FF]',
				useGuideIcons && 'shadow-[0_1px_4px_rgba(12,12,13,0.05)]',
				isDesktopPrestart
					? 'relative flex min-w-0 items-center gap-3'
					: 'flex h-[75px] w-full items-center gap-3'
			)}
		>
			{useGuideIcons ? (
				<ResourceGuideIconBadge icon={icon} iconClassName={iconClassName} imageUrl={iconUrl} />
			) : (
				<PrestartResourceIconBadge
					icon={icon as keyof typeof PRESTART_RESOURCE_ICONS}
					iconClassName={iconClassName}
				/>
			)}

			<div className="min-w-0 flex-1">
				<h3
					className={cn(
						'text-base font-semibold leading-[18px] tracking-[-0.448px] text-[#0A0A0A]',
						isDesktopPrestart && 'whitespace-nowrap'
					)}
				>
					{title}
				</h3>
				<p
					className={cn(
						'mt-1 text-sm leading-5 tracking-[-0.392px] text-[#474747]',
						isDesktopPrestart ? 'whitespace-nowrap' : 'truncate'
					)}
				>
					{description}
				</p>
			</div>

			{useGuideIcons ? (
				<ArrowRightGuideIcon className="size-5 shrink-0" aria-hidden="true" />
			) : (
				<ArrowRightPrestartIcon
					className={cn('h-3 w-4 shrink-0', isDesktopPrestart && 'absolute right-4 top-5')}
					aria-hidden="true"
				/>
			)}
		</a>
	);
}
